/**
 * Wires up the "Edit" buttons rendered by blocks/configuration-summary.liquid
 * (class `.configuration-summary__edit`, `data-edit-target="<option name>"`)
 * so clicking one jumps back to, and focuses, the matching configurator
 * option group (`[data-configurator-group="<option name>"]`) rendered by
 * blocks/configurator-options.liquid.
 *
 * Deliberately its own file, not appended to assets/configurator-size-matrix.js:
 * that file's single responsibility is the Width/Depth/Height size composer,
 * and this is an unrelated concern (a delegated document click that scrolls
 * to and focuses a DIFFERENT block's fieldset). Loaded as a <script> directly
 * from blocks/configuration-summary.liquid, mirroring how
 * configurator-size-matrix.js is loaded from blocks/configurator-options.liquid.
 *
 * Event delegation on `document`, not a listener on the buttons themselves:
 * two independent things rebuild the DOM around these buttons on every
 * `variant:update`, so nothing here can safely be a stable node to bind to --
 * - assets/configuration-summary.js replaces the summary block's entire
 *   `[ref="card"]` (which contains every one of these buttons) with fresh
 *   server-rendered HTML on every variant change.
 * - assets/configurator-size-matrix.js rebuilds the Width/Depth/Height
 *   fieldsets (the jump TARGETS, not the buttons) on the same event.
 * A single delegated listener on `document` survives both without needing to
 * re-bind after either morph.
 */

/**
 * Returns the first <input> inside `group` that isn't nested under a
 * `[hidden]` ancestor, or null. "First control" per the task's accessibility
 * requirement -- not "checked control": a jump should always land somewhere
 * focusable, and the group's first control is the predictable, testable
 * choice.
 *
 * The `[hidden]` check matters specifically for the combined-size fieldset:
 * once configurator-size-matrix.js successfully parses a size string, it
 * hides that fieldset's own radio list (`[data-size-fallback]`, via the
 * `hidden` attribute -- see that file's `init()`) and appends new
 * Width/Depth/Height fieldsets alongside it instead. The original fieldset's
 * inputs are still in the DOM at that point, but `hidden`-under-an-ancestor
 * elements cannot take focus, so they must not be returned here.
 * @param {Element | null} group
 * @returns {HTMLInputElement | null}
 */
function firstReachableInput(group) {
  if (!group) return null;
  const inputs = group.querySelectorAll('input');
  for (const input of inputs) {
    // `disabled` is checked alongside `[hidden]` for the same reason: a disabled
    // control cannot take focus either, so returning one would make the jump's
    // focus() a silent no-op for keyboard users. I4 makes this reachable for the
    // size fallback list (previously only Finish/Hardware could render a
    // disabled input, per Ruling 22), and a group whose FIRST value is sold out
    // is exactly the case that would have broken.
    if (!input.disabled && !input.closest('[hidden]')) return /** @type {HTMLInputElement} */ (input);
  }
  return null;
}

/**
 * Resolves a `data-edit-target` option name to the fieldset to jump to.
 *
 * Two things can go wrong with a plain
 * `document.querySelector('[data-configurator-group="<name>"]')` lookup,
 * both called out by the task brief. Both are logged with console.warn (not
 * thrown, nothing rendered to the customer) so a config-drift bug -- e.g. a
 * mistyped `hardware_option_name` setting -- shows up somewhere instead of
 * producing a real "Edit" button that silently does nothing forever:
 *
 * 1. The group may not exist at all -- e.g. a stale/misconfigured
 *    `hardware_option_name` setting, or any block config drift. Returns null;
 *    the caller no-ops rather than throwing or jumping somewhere wrong.
 * 2. The group may exist but be functionally dead -- specifically the
 *    combined-size fieldset once its own radio list has been hidden by
 *    configurator-size-matrix.js (see firstReachableInput above). In that
 *    case this redirects to the first generated Width/Depth/Height axis
 *    fieldset, which lives alongside the original inside the same
 *    <configurator-size-matrix> element, instead of scrolling to a fieldset
 *    with nothing focusable inside it. If that fallback also comes up empty,
 *    this returns the original (still-scrollable, non-focusable) group as a
 *    last resort -- see the comment at that return site for why this case is
 *    believed unreachable for "Select Size" specifically but is handled
 *    anyway as a backstop for any other group.
 * @param {string} name
 * @returns {Element | null}
 */
function resolveTarget(name) {
  const direct = document.querySelector(`[data-configurator-group="${CSS.escape(name)}"]`);
  if (!direct) {
    // Diagnostic only -- control flow is unchanged, this still returns null
    // and the caller still no-ops. Without it, a mistyped `hardware_option_name`
    // (or any other drift between a data-edit-target value and the real
    // data-configurator-group values configurator-options.liquid /
    // configurator-size-matrix.js render) produces a real, visible "Edit"
    // button that silently does nothing on every click, forever, with no
    // console output and no way to diagnose it -- an undiagnosable support
    // ticket. Matches the existing console.warn precedent for an unmatched-
    // value case in assets/gift-card-recipient-form.js:193.
    console.warn(`configurator-edit-jump: no group found for "${name}"`);
    return null;
  }
  if (firstReachableInput(direct)) return direct;

  const axis = direct
    .closest('configurator-size-matrix')
    ?.querySelector('.configurator-size-matrix__axes [data-configurator-group]');
  if (axis && firstReachableInput(axis)) return axis;

  // Second failure mode: the group exists, but neither it nor the size-axis
  // fallback has anything focusable inside it. Under
  // configurator-size-matrix.js's actual state machine this shouldn't happen
  // for the "Select Size" group specifically -- its own fallback list is
  // hidden only when render() just built at least one axis fieldset with
  // real inputs, so one of the two branches above always succeeds for that
  // one group (see firstReachableInput's doc comment). This is a backstop
  // for any OTHER group ending up in this state -- a future markup change,
  // or a merchant customization that hides a fieldset's inputs some other
  // way. The scroll below still happens (better than nothing for a sighted
  // user), but a keyboard user's focus will not move, which is the exact
  // accessibility requirement this feature exists to satisfy, so it gets the
  // same diagnostic treatment as the missing-group case above rather than
  // failing silently.
  console.warn(`configurator-edit-jump: group "${name}" has no focusable control to move focus to`);
  return direct;
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const button = event.target.closest('.configuration-summary__edit');
  if (!button) return;

  const name = /** @type {HTMLElement} */ (button).dataset.editTarget;
  if (!name) return;

  const target = resolveTarget(name);
  if (!target) return;

  // CRITICAL LAYOUT CONSTRAINT (Task 6 carryover): the site header is sticky
  // at `--header-height`, and scroll-margin-top on `.configurator-group`
  // (assets/configurator.css) reserves `calc(var(--header-height) +
  // var(--padding-lg))` of clearance above every jump target -- the same
  // offset already used for the sticky product-image pane -- so `block:
  // 'start'` lands the group fully below the header instead of underneath
  // it. scroll-margin-top was chosen over computing the offset in JS here
  // because it is declarative, applies uniformly to every `.configurator-group`
  // (both the server-rendered fieldsets and the ones
  // configurator-size-matrix.js generates client-side, since both use that
  // same class), and needs no re-measuring if the header height ever changes.
  //
  // behavior: 'instant' is deliberate, not a stylistic default -- VERIFIED,
  // not assumed. Smooth scrolling is broken page-wide on this template: the
  // root element carries `scroll-behavior: smooth`, and on a live measurement
  // (target 979px above the viewport, scrollY 1577) neither
  // `scrollIntoView({behavior:'smooth', block:'start'})` nor
  // `scrollIntoView({block:'start'})` (which resolves to smooth via that root
  // rule) moved scrollY at all, and neither did
  // `window.scrollTo({top, behavior:'smooth'})` -- something on the page
  // cancels every smooth scroll, for both APIs, unconditionally. Only
  // `behavior: 'instant'` actually moves the viewport; the same measurement
  // confirmed it lands the target at exactly 108px, i.e. precisely the
  // scroll-margin-top offset above, so the offset itself needs no change.
  // This is a pre-existing, page-wide bug and out of scope for this file --
  // tracked separately. Do not "fix" this back to 'smooth': that silently
  // breaks the jump again (focus still moves; the scroll just never happens).
  // No prefers-reduced-motion branch: both branches would resolve to the same
  // instant jump today, so a branch here would only be misleading.
  target.scrollIntoView({ behavior: 'instant', block: 'start' });

  // Move focus for keyboard users too, not just scroll for sighted ones.
  // preventScroll: true so this focus call doesn't fight the scrollIntoView
  // above (some browsers scroll to bring a newly-focused element into view).
  firstReachableInput(target)?.focus({ preventScroll: true });
});
